import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  catName: string;
  shelterName: string;
}

export default function AdoptionInquiryModal({ isOpen, onClose, catName, shelterName }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const message = formData.get('message') as string;
    if (message && message.length > 500) return;

    setIsSubmitting(true);
    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncoded.append(key, value.toString());
      });
      urlEncoded.append('cat_name', catName);
      urlEncoded.append('shelter_name', shelterName);

      await fetch('https://readdy.ai/api/form/d6dlb1uqd308q1a0a51g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
      setIsSuccess(true);
    } catch {
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSuccess(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[80] backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🐱</span>
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-gray-900">
                      {isSuccess ? 'Inquiry Sent!' : `Adopt ${catName}`}
                    </h3>
                    {!isSuccess && (
                      <p className="font-body text-xs text-gray-400 mt-0.5">via {shelterName}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-all cursor-pointer"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>

              {/* Body */}
              <div className="p-5">
                {isSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, type: 'spring' }}
                    className="text-center py-8"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                      className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5 relative"
                    >
                      <motion.i
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="ri-check-line text-4xl text-emerald-600"
                      ></motion.i>
                      <motion.div
                        initial={{ scale: 1, opacity: 0.8 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{ duration: 1, repeat: Infinity }}
                        className="absolute inset-0 bg-emerald-200 rounded-full"
                      ></motion.div>
                    </motion.div>
                    <motion.h4
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="font-heading font-bold text-2xl text-gray-900 mb-3"
                    >
                      Thank You! 🎉
                    </motion.h4>
                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="font-body text-sm text-gray-500 max-w-xs mx-auto leading-relaxed"
                    >
                      Your inquiry about <strong className="text-gray-900">{catName}</strong> has been sent to <strong className="text-gray-900">{shelterName}</strong>. They&apos;ll reach out to you soon!
                    </motion.p>
                    <motion.button
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      onClick={handleClose}
                      className="mt-8 px-8 py-3 bg-accent hover:bg-accent/90 text-white font-heading font-semibold text-sm rounded-lg transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg"
                    >
                      Continue Browsing
                    </motion.button>
                  </motion.div>
                ) : (
                  <form
                    ref={formRef}
                    id="adoption-inquiry-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block font-body text-xs font-medium text-gray-600 mb-1">First Name *</label>
                        <input
                          type="text"
                          name="first_name"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm font-body focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                          placeholder="Jane"
                        />
                      </div>
                      <div>
                        <label className="block font-body text-xs font-medium text-gray-600 mb-1">Last Name *</label>
                        <input
                          type="text"
                          name="last_name"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm font-body focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-body text-xs font-medium text-gray-600 mb-1">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm font-body focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                        placeholder="jane@example.com"
                      />
                    </div>

                    <div>
                      <label className="block font-body text-xs font-medium text-gray-600 mb-1">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm font-body focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                        placeholder="(503) 555-0000"
                      />
                    </div>

                    <div>
                      <label className="block font-body text-xs font-medium text-gray-600 mb-1">Living Situation</label>
                      <select
                        name="living_situation"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm font-body focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all cursor-pointer"
                      >
                        <option value="">Select...</option>
                        <option value="House">House</option>
                        <option value="Apartment">Apartment</option>
                        <option value="Condo">Condo</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-body text-xs font-medium text-gray-600 mb-1">Other Pets at Home</label>
                      <select
                        name="other_pets"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm font-body focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all cursor-pointer"
                      >
                        <option value="">Select...</option>
                        <option value="None">None</option>
                        <option value="Cat(s)">Cat(s)</option>
                        <option value="Dog(s)">Dog(s)</option>
                        <option value="Both cats and dogs">Both cats and dogs</option>
                        <option value="Other pets">Other pets</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-body text-xs font-medium text-gray-600 mb-1">
                        Message <span className="text-gray-400">(optional)</span>
                      </label>
                      <textarea
                        name="message"
                        rows={3}
                        maxLength={500}
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm font-body focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all resize-none"
                        placeholder="Tell the shelter a bit about yourself and why you'd love to adopt this cat..."
                      ></textarea>
                      <p className="font-body text-xs text-gray-400 mt-1">Max 500 characters</p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-3 bg-accent hover:bg-accent/90 text-white font-heading font-bold text-sm rounded-lg transition-all duration-300 hover:scale-[1.02] whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="ri-heart-line"></i>
                          Submit Adoption Inquiry
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}